// AGC: Forecast - Extended weather forecast from Open-Meteo API
// AGC: Tier-based days: FREE (7), PRO/PREMIUM (14)
import React, { useState, useMemo, useEffect } from 'react'
import { ChevronDown, ChevronUp, Droplets, Wind, Sun, Crown, RefreshCw, MapPin } from 'lucide-react'
import { TopBar, GlassCard, Card } from '../shared'
import { formatTemperature, formatPercentage, formatWindSpeed } from '@droidfarm/shared'
import { useWeather } from '../../hooks/useWeather'
import { useFarms } from '../../context/FarmContext'
import { useLotes } from '../../context/LotesContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { WEATHER_CONDITIONS, type Screen, type DailyForecast, type Plot } from '../../types'

interface ForecastProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: Forecast - Extended weather forecast with expandable details
 * AGC: Uses real Open-Meteo API data via useWeather hook
 */
export const Forecast: React.FC<ForecastProps> = ({ onNavigate, onBack }) => {
  const { t } = useTranslation()
  const { farms, currentFarm, setCurrentFarmById } = useFarms()
  const { plots } = useLotes()
  const { openUpgradeModal } = useSubscription()

  const [showLocationSelector, setShowLocationSelector] = useState(false)
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null)
  const [expandedDay, setExpandedDay] = useState<string | null>(null)

  // AGC: Get lots for the current farm
  const currentFarmLots = useMemo(() => {
    if (!currentFarm) return []
    return plots.filter(p => p.fieldId === currentFarm.id || p.farmId === currentFarm.id)
  }, [plots, currentFarm])

  // AGC: Compute polygon center from lot coordinates
  const getLotCenter = (lot: Plot): { lat: number; lon: number } | undefined => {
    if (!lot.coordinates || lot.coordinates.length === 0) return undefined
    const lat = lot.coordinates.reduce((sum, c) => sum + c[0], 0) / lot.coordinates.length
    const lon = lot.coordinates.reduce((sum, c) => sum + c[1], 0) / lot.coordinates.length
    if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return { lat, lon }
    return undefined
  }

  const selectedLot = useMemo(() => {
    if (!selectedLotId) return null
    return currentFarmLots.find(l => l.id === selectedLotId) || null
  }, [selectedLotId, currentFarmLots])

  // AGC: Get coordinates from selected lot or farm
  const farmCoordinates = useMemo(() => {
    if (selectedLot) {
      const center = getLotCenter(selectedLot)
      if (center) return center
    }
    if (currentFarm?.coordinates && Array.isArray(currentFarm.coordinates)) {
      const [lat, lng] = currentFarm.coordinates
      if (lat && lng && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lon: lng }
      }
    }
    return undefined
  }, [currentFarm, selectedLot])

  const { daily, location, isLoading, error, tier, forecastDays, refetch } = useWeather({
    coordinates: farmCoordinates,
  })

  // AGC: Display name for location selector
  const locationDisplayName = useMemo(() => {
    if (selectedLot) return selectedLot.name
    return currentFarm?.name || location || t('weather.locationUnknown')
  }, [selectedLot, currentFarm, location, t])

  // AGC: Clear selected lot when farm changes
  useEffect(() => {
    setSelectedLotId(null)
  }, [currentFarm?.id])

  // AGC: Get weather icon
  const getWeatherIcon = (condition: string): string => {
    return WEATHER_CONDITIONS.find(c => c.id === condition)?.icon || '☁️'
  }

  // AGC: Toggle expanded day
  const toggleDay = (date: string) => {
    setExpandedDay(expandedDay === date ? null : date)
  }

  // AGC: Get today's date string
  const today = new Date().toISOString().split('T')[0]

  // AGC: Loading state
  if (isLoading && daily.length === 0) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]">
        <TopBar 
          title={t('weather.forecastTitle') || `Previsão ${forecastDays} Dias`}
          subtitle={location || t('weather.loading') || 'Carregando...'}
          onBack={onBack}
        />
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-[#0B5F4A] animate-spin" />
        </div>
      </div>
    )
  }

  // AGC: Error state
  if (error && daily.length === 0) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]">
        <TopBar 
          title={t('weather.forecastTitle') || `Previsão ${forecastDays} Dias`}
          subtitle={t('weather.error') || 'Erro'}
          onBack={onBack}
        />
        <div className="px-4 py-8">
          <GlassCard className="text-center">
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => refetch(true)}
              className="px-4 py-2 bg-[#0B5F4A] text-white rounded-lg"
            >
              {t('weather.retry') || 'Tentar novamente'}
            </button>
          </GlassCard>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Top Bar */}
      <TopBar
        title={t('weather.forecastTitle', { days: forecastDays }) || `Previsão ${forecastDays} Dias`}
        onBack={onBack}
        actions={
          <button
            onClick={() => refetch(true)}
            disabled={isLoading}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* AGC: Location selector - same pattern as WeatherDashboard */}
      <div className="px-4 py-2 bg-[#fafbfc] dark:bg-gray-900 relative">
        <div className="relative">
          <button
            onClick={() => setShowLocationSelector(!showLocationSelector)}
            className="flex items-center gap-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg px-2 py-1.5 -ml-2 transition-colors"
          >
            <MapPin className="w-4 h-4 text-emerald-600" />
            <span className="text-slate-500 text-sm">{t('weather.place')}:</span>
            <span className="text-slate-900 dark:text-slate-50 font-bold text-sm max-w-[200px] truncate">{locationDisplayName}</span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showLocationSelector ? 'rotate-180' : ''}`} />
          </button>

          {showLocationSelector && (
            <Card variant="elevated" padding="none" className="absolute top-full left-0 mt-1 z-50 min-w-[220px] max-h-[300px] overflow-y-auto">
              {/* AGC: Farms section */}
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                {t('weather.farmsSection')}
              </div>
              {farms.map(farm => (
                <button
                  key={farm.id}
                  onClick={() => {
                    setCurrentFarmById(farm.id)
                    setSelectedLotId(null)
                    setShowLocationSelector(false)
                  }}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors ${
                    farm.id === currentFarm?.id && !selectedLotId
                      ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
                      : 'text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{farm.name}</span>
                </button>
              ))}

              {/* AGC: Lots section */}
              {currentFarmLots.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 border-b border-t border-slate-200 dark:border-slate-700">
                    {t('weather.lotsSection')}
                  </div>
                  {currentFarmLots.map(lot => (
                    <button
                      key={lot.id}
                      onClick={() => {
                        setSelectedLotId(lot.id)
                        setShowLocationSelector(false)
                      }}
                      className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors ${
                        selectedLotId === lot.id
                          ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
                          : 'text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="w-4 h-4 flex-shrink-0 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-sm bg-emerald-500" />
                      </div>
                      <span className="truncate">{lot.name}</span>
                    </button>
                  ))}
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* AGC: Tier upgrade banner for FREE users */}
      {tier === 'free' && (
        <div className="px-4 py-3">
          <GlassCard 
            className="bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200 cursor-pointer"
            onClick={() => openUpgradeModal('fields')}
            hover
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-yellow-800">
                  {t('weather.upgradeTitle') || 'Upgrade para PRO'}
                </p>
                <p className="text-sm text-yellow-700">
                  {t('weather.upgradeDescription') || 'Desbloqueie previsão de 14 dias e recursos avançados'}
                </p>
              </div>
              <ChevronDown className="w-5 h-5 text-yellow-600 rotate-[-90deg]" />
            </div>
          </GlassCard>
        </div>
      )}

      {/* AGC: Daily Forecast List */}
      <div className="px-4 py-4 space-y-3">
        {daily.map((day, index) => (
          <ForecastDayCard
            key={day.date}
            day={day}
            isToday={day.date === today || index === 0}
            isExpanded={expandedDay === day.date}
            onToggle={() => toggleDay(day.date)}
            getWeatherIcon={getWeatherIcon}
            t={t}
          />
        ))}

        {/* AGC: Show locked days for FREE tier */}
        {tier === 'free' && daily.length < 14 && (
          <GlassCard 
            className="bg-gray-50 border-dashed border-2 border-gray-200 cursor-pointer"
            onClick={() => openUpgradeModal('fields')}
            hover
          >
            <div className="flex items-center justify-center gap-3 py-4">
              <Crown className="w-5 h-5 text-yellow-500" />
              <span className="text-gray-500">
                {t('weather.moreWith') || '+7 dias com'} <span className="font-semibold text-[#0B5F4A]">PRO</span>
              </span>
            </div>
          </GlassCard>
        )}
      </div>

      {/* AGC: Legend */}
      <div className="px-4 pb-6">
        <GlassCard className="bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">{t('weather.legend') || 'Legenda'}</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-500" />
              <span className="text-gray-600">{t('weather.rainChance') || 'Chance de Chuva'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Wind className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">{t('weather.windSpeed') || 'Velocidade do Vento'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-600">{t('weather.uvIndex') || 'Índice UV'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-500">🌡️</span>
              <span className="text-gray-600">{t('weather.humidity') || 'Umidade'}</span>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Open-Meteo Attribution */}
      <div className="px-4 pb-6 text-center">
        <a 
          href="https://open-meteo.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          {t('weather.attribution') || 'Dados meteorológicos por Open-Meteo.com'}
        </a>
      </div>
    </div>
  )
}

/**
 * AGC: Forecast Day Card Component
 */
interface ForecastDayCardProps {
  day: DailyForecast
  isToday: boolean
  isExpanded: boolean
  onToggle: () => void
  getWeatherIcon: (condition: string) => string
  t: (key: string) => string
}

const ForecastDayCard: React.FC<ForecastDayCardProps> = ({
  day,
  isToday,
  isExpanded,
  onToggle,
  getWeatherIcon,
  t,
}) => {
  const conditionName = WEATHER_CONDITIONS.find(c => c.id === day.condition)?.name || day.condition

  return (
    <GlassCard padding="none" className={isToday ? 'ring-2 ring-[#0B5F4A]' : ''}>
      {/* AGC: Main row (always visible) */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <span className="text-3xl">{getWeatherIcon(day.condition)}</span>
          <div className="text-left">
            <p className="font-medium text-gray-900">
              {isToday ? (t('weather.today') || 'Hoje') : day.dayOfWeek}
            </p>
            <p className="text-sm text-gray-500">{conditionName}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* AGC: Temperature range */}
          <div className="text-right">
            <span className="text-lg font-bold text-gray-900">
              {day.temperatureMax}°
            </span>
            <span className="text-lg text-gray-400 ml-1">
              {day.temperatureMin}°
            </span>
          </div>

          {/* AGC: Rain probability */}
          {day.precipitationProbability > 20 && (
            <div className="flex items-center gap-1 text-blue-500">
              <Droplets className="w-4 h-4" />
              <span className="text-sm font-medium">{day.precipitationProbability}%</span>
            </div>
          )}

          {/* AGC: Expand icon */}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* AGC: Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 pt-4">
            {/* AGC: Precipitation */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Droplets className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('weather.rain') || 'Chuva'}</p>
                <p className="font-medium text-gray-900">{day.precipitationProbability}%</p>
              </div>
            </div>

            {/* AGC: Wind */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                <Wind className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('weather.wind') || 'Vento'}</p>
                <p className="font-medium text-gray-900">{formatWindSpeed(day.windSpeed)}</p>
              </div>
            </div>

            {/* AGC: Humidity */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                <span className="text-sm">💧</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('weather.humidity') || 'Umidade'}</p>
                <p className="font-medium text-gray-900">{formatPercentage(day.humidity)}</p>
              </div>
            </div>

            {/* AGC: UV Index */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Sun className="w-4 h-4 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500">UV</p>
                <p className="font-medium text-gray-900">
                  {day.uvIndex}
                  <span className="text-xs text-gray-500 ml-1">
                    ({day.uvIndex >= 8 ? 'Muito Alto' : day.uvIndex >= 6 ? 'Alto' : 'Moderado'})
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* AGC: Spray recommendation for the day */}
          <div className="mt-4 p-3 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{t('weather.spray') || 'Pulverização'}: </span>
              {day.precipitationProbability > 50 
                ? (t('weather.sprayNotRecommended') || '❌ Não recomendado devido à alta chance de chuva')
                : day.windSpeed > 15
                ? (t('weather.sprayWindWarning') || '⚠️ Atenção com a velocidade do vento')
                : (t('weather.sprayFavorableConditions') || '✅ Condições potencialmente favoráveis')
              }
            </p>
          </div>
        </div>
      )}
    </GlassCard>
  )
}

export default Forecast
